/**
 * The six request processors of LSEG's FX functions (RDP.FxSpot,
 * RDP.FxSwpToSwp, RDP.FxSwpToDep, RDP.FxDepToSwp, RDP.FxZcToFra, RDP.FxInfo),
 * ported from Workspace's Excel add-in (Refinitiv for Office 26.30.2). Each
 * reads the Excel arguments in LSEG's order and builds the request item LSEG
 * sends to `analytics/functions/v1/fx/<endpoint>` (`rates/zc-to-fra` for
 * FxZcToFra), batched as `{ universe: [item] }` by the interface.
 *
 * Unlike the date functions, LSEG sends no `fields` for these: Fields is
 * checked (LSEG's validation texts) and kept for the response layout only.
 * A value that is falsy (blank, 0, "") is left out of the request, as LSEG's
 * `x && { key: x }` does.
 */
import { AF_ARG, BaseDateProcessor } from "./baseProcessor";
import { CURVE_POINT_UNITS, ForexUtils, ZcCurve, ZcCurveCellRangeProcessor } from "./forex";
import { withoutEmptyValues } from "./utils";

/** The arguments the four cross-code functions start with: cross code, date, tenors, two spot overrides. */
// LSEG: the shared head of the request objects of SpotProcessor_1180, SwapToSwapProcessor_17714,
// SwapToDepProcessor_21818 and DepToSwapProcessor_27474 (prepareRequestInput)
function fxRequestHead(
  p: { fxCrossCode: string; marketDataDate: unknown; tenors?: string[] },
  spotCcy1: unknown,
  spotCcy2: unknown,
): Record<string, unknown> {
  return {
    ...(p.fxCrossCode && { fxCrossCode: p.fxCrossCode }),
    ...(p.marketDataDate ? { marketDataDate: p.marketDataDate } : {}),
    ...(p.tenors && { tenors: p.tenors }),
    ...(spotCcy1 ? { spotCcy1 } : {}),
    ...(spotCcy2 ? { spotCcy2 } : {}),
  };
}

// LSEG: SpotProcessor_1180.SpotProcessor
export class FxSpotProcessor extends BaseDateProcessor {
  static readonly FIELDS = ["SpotDate", "SpotCcy1Bid", "SpotCcy1Ask", "SpotCcy2Bid", "SpotCcy2Ask", "SpotCcy1Ccy2Bid", "SpotCcy1Ccy2Ask"];
  private readonly forexUtils = new ForexUtils();

  constructor(args: unknown[]) {
    super(args);
    this.setArgIndexes([AF_ARG.fxCrossCode, AF_ARG.marketDataDate, AF_ARG.spotCcy1, AF_ARG.spotCcy2, AF_ARG.fields]);
  }

  // LSEG: SpotProcessor_1180.prepareRequestInput
  prepareRequestInput(): Record<string, unknown> {
    const fxCrossCode = this.forexUtils.getFxCrossCode(this.getArg(AF_ARG.fxCrossCode));
    const marketDataDate = this.getAdfinDate(this.getArg(AF_ARG.marketDataDate), { marketDataDate: "Market Data Date" }, false);
    const spotCcy1 = this.forexUtils.getSpotCcy(this.getArg(AF_ARG.spotCcy1), "SpotCcy1");
    const spotCcy2 = this.forexUtils.getSpotCcy(this.getArg(AF_ARG.spotCcy2), "SpotCcy2");
    this.setOutputFields(this.getArg(AF_ARG.fields), FxSpotProcessor.FIELDS, "Fields");
    return fxRequestHead({ fxCrossCode, marketDataDate }, spotCcy1, spotCcy2);
  }
}

// LSEG: SwapToSwapProcessor_17714.SwapToSwapProcessor
export class FxSwpToSwpProcessor extends BaseDateProcessor {
  static readonly FIELDS = [
    "Tenor",
    "StartDate",
    "EndDate",
    "SwapCcy1Bid",
    "SwapCcy1Ask",
    "SwapCcy2Bid",
    "SwapCcy2Ask",
    "SwapCcy1Ccy2Bid",
    "SwapCcy1Ccy2Ask",
    "OutrightCcy1Ccy2Bid",
    "OutrightCcy1Ccy2Ask",
  ];
  private readonly forexUtils = new ForexUtils();

  constructor(args: unknown[]) {
    super(args);
    this.setArgIndexes([
      AF_ARG.fxCrossCode,
      AF_ARG.marketDataDate,
      AF_ARG.tenors,
      AF_ARG.spotCcy1,
      AF_ARG.spotCcy2,
      AF_ARG.swapPointsCcy1,
      AF_ARG.swapPointsCcy2,
      AF_ARG.fields,
    ]);
  }

  // LSEG: SwapToSwapProcessor_17714.prepareRequestInput
  prepareRequestInput(): Record<string, unknown> {
    const fxCrossCode = this.forexUtils.getFxCrossCode(this.getArg(AF_ARG.fxCrossCode));
    const marketDataDate = this.getAdfinDate(this.getArg(AF_ARG.marketDataDate), { marketDataDate: "Market Data Date" }, false);
    const tenors = this.getTenors(this.getArg(AF_ARG.tenors), false);
    const spotCcy1 = this.forexUtils.getSpotCcy(this.getArg(AF_ARG.spotCcy1), "SpotCcy1");
    const spotCcy2 = this.forexUtils.getSpotCcy(this.getArg(AF_ARG.spotCcy2), "SpotCcy2");
    const swapPointsCcy1 = this.forexUtils.getForexOverrides(this.getArg(AF_ARG.swapPointsCcy1), "SwapPointsCcy1");
    const swapPointsCcy2 = this.forexUtils.getForexOverrides(this.getArg(AF_ARG.swapPointsCcy2), "SwapPointsCcy2");
    this.setOutputFields(this.getArg(AF_ARG.fields), FxSwpToSwpProcessor.FIELDS, "Fields", "Tenor");
    return {
      ...fxRequestHead({ fxCrossCode, marketDataDate, tenors }, spotCcy1, spotCcy2),
      ...(swapPointsCcy1 && { swapPointsCcy1 }),
      ...(swapPointsCcy2 && { swapPointsCcy2 }),
    };
  }
}

/** FxSwpToDep's tenors when none are given. */
// LSEG: SwapToDepProcessor_21818.getSwpToDepTenors (its default)
const SWP_TO_DEP_DEFAULT_TENORS = "ON,TN,SN,SW,1M,2M,3M,6M,9M,1Y,2Y";

// LSEG: SwapToDepProcessor_21818.SwapToDepProcessor
export class FxSwpToDepProcessor extends BaseDateProcessor {
  static readonly FIELDS = [
    "Tenor",
    "StartDate",
    "EndDate",
    "SwapCcy1Bid",
    "SwapCcy1Ask",
    "SwapCcy2Bid",
    "SwapCcy2Ask",
    "DepCcy1Bid",
    "DepCcy1Ask",
    "DepCcy2Bid",
    "DepCcy2Ask",
  ];
  private readonly forexUtils = new ForexUtils();

  constructor(args: unknown[]) {
    super(args);
    this.setArgIndexes([
      AF_ARG.fxCrossCode,
      AF_ARG.marketDataDate,
      AF_ARG.tenors,
      AF_ARG.spotCcy1,
      AF_ARG.spotCcy2,
      AF_ARG.swapPointsCcy1,
      AF_ARG.swapPointsCcy2,
      AF_ARG.depRatesCcy1,
      AF_ARG.fields,
    ]);
  }

  // LSEG: SwapToDepProcessor_21818.prepareRequestInput
  prepareRequestInput(): Record<string, unknown> {
    const fxCrossCode = this.forexUtils.getFxCrossCode(this.getArg(AF_ARG.fxCrossCode));
    const marketDataDate = this.getAdfinDate(this.getArg(AF_ARG.marketDataDate), { marketDataDate: "Market Data Date" }, false);
    const tenors = this.getSwpToDepTenors(this.getArg(AF_ARG.tenors));
    const spotCcy1 = this.forexUtils.getSpotCcy(this.getArg(AF_ARG.spotCcy1), "SpotCcy1");
    const spotCcy2 = this.forexUtils.getSpotCcy(this.getArg(AF_ARG.spotCcy2), "SpotCcy2");
    const swapPointsCcy1 = this.forexUtils.getForexOverrides(this.getArg(AF_ARG.swapPointsCcy1), "SwapPointsCcy1");
    const swapPointsCcy2 = this.forexUtils.getForexOverrides(this.getArg(AF_ARG.swapPointsCcy2), "SwapPointsCcy2");
    const depRatesCcy1 = this.forexUtils.getForexOverrides(this.getArg(AF_ARG.depRatesCcy1), "DepRatesCcy1");
    this.setOutputFields(this.getArg(AF_ARG.fields), FxSwpToDepProcessor.FIELDS, "Fields", "Tenor");
    return {
      ...fxRequestHead({ fxCrossCode, marketDataDate, tenors }, spotCcy1, spotCcy2),
      ...(swapPointsCcy1 && { swapPointsCcy1 }),
      ...(swapPointsCcy2 && { swapPointsCcy2 }),
      ...(depRatesCcy1 && { depRatesCcy1 }),
    };
  }

  // LSEG: SwapToDepProcessor_21818.getSwpToDepTenors
  private getSwpToDepTenors(value: unknown): string[] | undefined {
    return this.getTenors(this.adFinUtils.isEmpty(value) ? SWP_TO_DEP_DEFAULT_TENORS : value, false);
  }
}

// LSEG: DepToSwapProcessor_27474.DepToSwapProcessor
export class FxDepToSwpProcessor extends BaseDateProcessor {
  static readonly FIELDS = [
    "Tenor",
    "StartDate",
    "EndDate",
    "DepCcy1Bid",
    "DepCcy1Ask",
    "DepCcy2Bid",
    "DepCcy2Ask",
    "SwapCcy1Ccy2Bid",
    "SwapCcy1Ccy2Ask",
    "OutrightCcy1Ccy2Bid",
    "OutrightCcy1Ccy2Ask",
  ];
  private readonly forexUtils = new ForexUtils();

  constructor(args: unknown[]) {
    super(args);
    this.setArgIndexes([
      AF_ARG.fxCrossCode,
      AF_ARG.marketDataDate,
      AF_ARG.tenors,
      AF_ARG.spotCcy1,
      AF_ARG.spotCcy2,
      AF_ARG.depRatesCcy1,
      AF_ARG.depRatesCcy2,
      AF_ARG.fields,
    ]);
  }

  // LSEG: DepToSwapProcessor_27474.prepareRequestInput
  prepareRequestInput(): Record<string, unknown> {
    const fxCrossCode = this.forexUtils.getFxCrossCode(this.getArg(AF_ARG.fxCrossCode));
    const marketDataDate = this.getAdfinDate(this.getArg(AF_ARG.marketDataDate), { marketDataDate: "Market Data Date" }, false);
    const tenors = this.getTenors(this.getArg(AF_ARG.tenors), false);
    const spotCcy1 = this.forexUtils.getSpotCcy(this.getArg(AF_ARG.spotCcy1), "SpotCcy1");
    const spotCcy2 = this.forexUtils.getSpotCcy(this.getArg(AF_ARG.spotCcy2), "SpotCcy2");
    const depRatesCcy1 = this.forexUtils.getForexOverrides(this.getArg(AF_ARG.depRatesCcy1), "DepRatesCcy1");
    const depRatesCcy2 = this.forexUtils.getForexOverrides(this.getArg(AF_ARG.depRatesCcy2), "DepRatesCcy2");
    this.setOutputFields(this.getArg(AF_ARG.fields), FxDepToSwpProcessor.FIELDS, "Fields", "Tenor");
    return {
      ...fxRequestHead({ fxCrossCode, marketDataDate, tenors }, spotCcy1, spotCcy2),
      ...(depRatesCcy1 && { depRatesCcy1 }),
      ...(depRatesCcy2 && { depRatesCcy2 }),
    };
  }
}

const DIGITS = /(\d+(?:\.\d+)?)/;

// LSEG: ZcToFraProcessor_93238.ZcToFraProcessor
export class FxZcToFraProcessor extends BaseDateProcessor {
  static readonly FIELDS = ["StartDate", "EndDate", "Fra"];
  private readonly zcCurveCellRangeProcessor = new ZcCurveCellRangeProcessor();

  constructor(args: unknown[]) {
    super(args);
    this.setArgIndexes([AF_ARG.zcCurve, AF_ARG.start, AF_ARG.end, AF_ARG.calculationDate, AF_ARG.dayCountBasis, AF_ARG.fields]);
  }

  // LSEG: ZcToFraProcessor_93238.prepareRequestInput
  prepareRequestInput(): Record<string, unknown> {
    const zcCurve = this.getZcCurve(this.getArg(AF_ARG.zcCurve));
    const start = this.getPeriod(this.getArg(AF_ARG.start), { start: "Start" }, true);
    const end = this.getPeriod(this.getArg(AF_ARG.end), { end: "End" }, true);
    const calculationDate = this.getAdfinDate(this.getArg(AF_ARG.calculationDate), { calculationDate: "Calculation Date" }, false);
    const dayCountBasis = this.getDayCountBasis(this.getArg(AF_ARG.dayCountBasis));
    this.setOutputFields(this.getArg(AF_ARG.fields), FxZcToFraProcessor.FIELDS, "Fields");
    return {
      ...(zcCurve && { zcCurve }),
      ...(start && { start }),
      ...(end && { end }),
      ...(calculationDate && { calculationDate }),
      ...(dayCountBasis && { dayCountBasis }),
    };
  }

  /**
   * "EUR", "code:EUR;curvePoints:{1M,2.5},{3M,2.6,RatePercent}", quoted or
   * not, or a range (ZcCurveCellRangeProcessor).
   * [A] A number is read as its text (LSEG fails on it with a TypeError).
   */
  // LSEG: ZcToFraProcessor_93238.getZcCurve
  private getZcCurve(value: unknown): ZcCurve | undefined {
    this.validateNotEmpty(this.getArg(AF_ARG.zcCurve), "ZcCurve");
    if (!value) return undefined;
    // LSEG: AFCellRangeProcessor_29882.processCellRangeZcCurve (a pass-through)
    if (Array.isArray(value)) return this.zcCurveCellRangeProcessor.processZcCurve(value);
    const curve: ZcCurve = { code: "", curvePoints: [] };
    const [code, points] = this.removeQuotesIfHasAny(`${value}`).split(";");
    curve.code = this.getZcCode(code);
    this.setZcCurvePoints(curve, points);
    return withoutEmptyValues(curve); // LSEG: ZcToFraProcessor_93238.clearEmptyZcCurveValues
  }

  // LSEG: ZcToFraProcessor_93238.setZcCurvePoints
  private setZcCurvePoints(curve: ZcCurve, text: string | undefined): void {
    const points = this.extractCurvePoints(text);
    if (!points) return;
    curve.curvePoints = points;
    this.adFinUtils.validate(!!curve.code && points.length > 0, "ZcCurve", "argument is mandatory");
  }

  /** "curvePoints:{1M,2.5},{3M,2.6,RatePercent}" (the label optional) -> the points. */
  // LSEG: ZcToFraProcessor_93238.extractCurvePoints
  private extractCurvePoints(text: string | undefined): Record<string, unknown>[] | undefined {
    if (!text) return undefined;
    this.adFinUtils.validate(text.includes("{"), "ZcCurve", "curvePoints argument is mandatory");
    const [label, ...points] = text.toLowerCase().split("{");
    const labelled = label.endsWith(":") ? label.slice(0, -1) === "curvepoints" : label === "curvepoints";
    this.adFinUtils.validate(!label || labelled, "ZcCurve", "incorrect curvePoints label");
    this.adFinUtils.validate(!!points, "ZcCurve", "mandatory curvePoints argument is missing");
    return points.map((point) => {
      const [tenorText, valueText, unitText] = point.replace(/\}?,?$/, "").split(",");
      const tenor = this.adFinUtils.isTenor(tenorText.toUpperCase()) ? tenorText.toUpperCase() : "";
      this.adFinUtils.validate(!!tenor, "ZcCurve", "tenor in curvePoints argument is missing");
      const value = DIGITS.test(`${valueText}`) ? +valueText : "";
      this.adFinUtils.validate(!!value, "ZcCurve", "mandatory value in curvePoints argument is missing");
      const unit = this.getValidatedUnit(unitText);
      return this.adFinUtils.getObjectWithNonEmptyValue({ tenor, value, unit });
    });
  }

  // LSEG: ZcToFraProcessor_93238.getValidatedUnit
  private getValidatedUnit(unit: string | undefined): string | undefined {
    if (!unit) return unit;
    const key = unit.toLowerCase();
    const validated = Object.prototype.hasOwnProperty.call(CURVE_POINT_UNITS, key) ? CURVE_POINT_UNITS[key] : "";
    this.adFinUtils.validate(
      !!validated,
      "ZcCurve",
      "provided optional unit in curvePoints argument is wrong. Should be either DiscountFactor or RatePercent",
    );
    return validated;
  }

  // LSEG: ZcToFraProcessor_93238.removeQuotesIfHasAny
  private removeQuotesIfHasAny(value: string): string {
    const trimmed = value.trim();
    return trimmed.startsWith("'") && trimmed.endsWith("'") ? trimmed.slice(1, -1) : trimmed;
  }

  /** "EUR" or "code:EUR". */
  // LSEG: ZcToFraProcessor_93238.getZcCode
  private getZcCode(text: string): string {
    if (text.includes(":")) {
      const [label, code] = text.split(":");
      this.adFinUtils.validate(label.toLowerCase() === "code", "ZcCurve", "wrong code label");
      this.adFinUtils.validate(!!code, "ZcCurve", "mandatory code value is missing");
      return code;
    }
    this.adFinUtils.validate(!!text, "ZcCurve", "mandatory code value is missing");
    return text;
  }
}

// LSEG: InfoProcessor_90872.InfoProcessor
export class FxInfoProcessor extends BaseDateProcessor {
  static readonly FIELDS = ["CurrencyCode", "CurrencyName", "FxSpotLag", "MmSpotLag", "Calendar", "QuotationMode", "MmYearBasis"];

  constructor(args: unknown[]) {
    super(args);
    this.setArgIndexes([AF_ARG.currencyCodes, AF_ARG.fields]);
  }

  // LSEG: InfoProcessor_90872.prepareRequestInput
  prepareRequestInput(): Record<string, unknown> {
    const currencyCodes = this.getCurrencyCodes(this.getArg(AF_ARG.currencyCodes), true);
    this.setOutputFields(this.getArg(AF_ARG.fields), FxInfoProcessor.FIELDS, "Fields", "CurrencyCode");
    return { ...(currencyCodes && { currencyCodes }) };
  }
}
